import { createContext, type PropsWithChildren, useContext } from 'react';
import type { UseFormReturn } from 'react-hook-form';

export class Subject<T> {
  private observers: Set<(data: T) => void> = new Set();

  public subscribe(observer: (data: T) => void): () => void {
    if (!this.observers.has(observer)) {
      this.observers.add(observer);
    }

    return () => this.unsubscribe(observer);
  }

  next(data: T): void {
    this.observers.forEach(observer => {
      observer(data);
    });
  }

  unsubscribe(observer: (data: T) => void): void {
    this.observers.delete(observer);
  }
}

type FormSubmitProviderProps = {
  submitClickCount: number;
  submitClicked: boolean;
  triggerMap?: Map<symbol, UseFormReturn<any>['trigger']>;
  isValidating$?: Subject<boolean>;
};

type FormSubmitContextProps = FormSubmitProviderProps;

const FormSubmitContext = createContext<FormSubmitContextProps>({
  submitClicked: false,
  submitClickCount: 0,
  triggerMap: new Map<symbol, UseFormReturn<any>['trigger']>(),
  isValidating$: new Subject<boolean>(),
});

export const useFormSubmitContext = () => useContext(FormSubmitContext);

export const FormSubmitProvider = ({ children, ...value }: PropsWithChildren<FormSubmitProviderProps>) => {
  return <FormSubmitContext.Provider value={value}>{children}</FormSubmitContext.Provider>;
};
